import React, { useState, useMemo } from 'react';
import { downloadCSV } from '../../../../utils/excelUtils';
import { useLang, useAutoTranslate } from '../../i18n';
import { keepInView } from '../shared/dom';
import { AuditTable } from '../shared/AuditTable';
import { DateRange } from '../shared/DateRange';
import { EmptyState } from '../shared/EmptyState';
import { localDateISO, daysAgoISO, fmtDateShort, datePosition } from '../../logic/dates';
import { filterAuditLog, auditRollup, auditTotals, auditToCsvRows, dueForReorder, auditWindow } from '../../logic/audit';
import { AUDIT_RANGE_OPTIONS, DEFAULT_AUDIT_RANGE, AUDIT_CSV_HEADERS, AUDIT_TOP_STORES, REORDER_SUGGEST_COUNT, EXPENSE_ROUTE } from '../../config/constants';

// ── Summary — "how often do we actually order this?" ─────────────────────────
//
// The raw log answers "what happened"; this view answers "so what do I do now".
// Every item that's been ordered on two separate days has a *learned* rhythm
// (its average gap), so the list can tell you what's due rather than making you
// work it out from a column of counts.

/**
 * Order rhythm as a timeline: one tick per order date across the selected window.
 * Evenly spaced ticks = a steady staple; a cluster then silence = something that
 * dropped off. Conveys count, spacing and recency in the space of one cell.
 */
function RhythmStrip({ dates, start, end, label }) {
  return (
    <span className="do-rhythm" title={label} aria-label={label}>
      {dates.map(d => (
        <i key={d} className="do-rhythm__tick" style={{ left: `${datePosition(d, start, end)}%` }} />
      ))}
    </span>
  );
}

/** Per-order detail for one item — every logged cycle, newest first. */
function OrderDetail({ row, showStore }) {
  const { t } = useLang();
  return (
    <div className="do-audit__detail">
      <span className="do-audit__detail-lbl">{t('audit.everyOrder', { n: row.entries.length })}</span>
      <div className="do-audit__detail-rows">
        {row.entries.map(e => {
          const pending = (e.status || 'completed') === 'pending';
          return (
            <div key={e.id} data-testid="order-entry" className={`do-audit__entry${pending ? ' do-audit__entry--pending' : ''}`}>
              <span className="do-audit__entry-date">{pending ? '○' : '✓'} {fmtDateShort(e.date)}</span>
              {e.qty && <span className="do-audit__entry-qty">{e.qty}</span>}
              {showStore && e.store && <span className="do-audit__entry-store">🏪 {e.store}</span>}
              {e.addedDate && e.addedDate !== e.date && (
                <span className="do-audit__entry-added">{t('audit.addedOn', { date: fmtDateShort(e.addedDate) })}</span>
              )}
              {e.by && <span className="do-audit__entry-by">{e.by}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The 📊 Summary of a checklist's history: one row per item over a date range, and the
 * "due to reorder" strip. `skipKeys` = names (normalized) never to call due — on the list
 * already, or deleted from the checklist. `onReorder(row)` puts one back on the list.
 */
export function ChecklistSummary({ config, log, skipKeys, onReorder }) {
  const { t, ln } = useLang();
  const verb = t(`checklist.verb.${config.id}`);
  const today = localDateISO();

  const [days, setDays] = useState(DEFAULT_AUDIT_RANGE);   // 0 = all time
  const [from, setFrom] = useState('');                    // custom bounds override the preset
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');

  const custom = !!(from || to);
  // Presets are inclusive of today, so "30 days" spans today and the 29 before it.
  const range = useMemo(
    () => (custom ? { from, to } : { from: days ? daysAgoISO(days - 1) : '', to: '' }),
    [custom, from, to, days],
  );
  const pickPreset = d => { setDays(d); setFrom(''); setTo(''); };

  const windowed = useMemo(() => filterAuditLog(log, { ...range, q }), [log, range, q]);
  const rows = useMemo(() => auditRollup(windowed, today), [windowed, today]);
  const totals = useMemo(() => auditTotals(windowed, rows), [windowed, rows]);
  const due = useMemo(() => dueForReorder(rows, { onList: skipKeys }), [rows, skipKeys]);
  const win = useMemo(() => auditWindow(rows, range, today), [rows, range, today]);
  useAutoTranslate(useMemo(() => rows.map(r => r.name), [rows]));

  const dueKeys = useMemo(() => new Set(due.map(r => r.key)), [due]);
  const showStore = !!config.hasStore;

  const cols = useMemo(() => {
    const base = [
      {
        key: 'name',
        label: t('col.item'),
        className: 'do-htable__item',
        cell: r => (
          <span className="do-audit__name">
            {dueKeys.has(r.key) && <span className="do-audit__due-dot" title={t('audit.dueNow')} />}
            {ln(r.name)}
            {r.pending > 0 && <span className="do-audit__pending" title={t('audit.pendingTitle', { n: r.pending })}>○{r.pending}</span>}
          </span>
        ),
      },
      {
        key: 'orders',
        label: t('audit.colTimes'),
        sortVal: r => r.orders,
        cell: r => <span className="do-audit__count">{r.orders}</span>,
      },
      {
        key: 'rhythm',
        label: t('audit.colRhythm'),
        sortVal: r => r.lastDate,
        cell: r => (r.dates.length
          ? <RhythmStrip dates={r.dates} start={win.start} end={win.end}
              label={t('audit.rhythmLabel', { n: r.dates.length, from: fmtDateShort(win.start), to: fmtDateShort(win.end) })} />
          : '—'),
      },
      {
        key: 'avgGapDays',
        label: t('audit.colEvery'),
        sortVal: r => (r.avgGapDays == null ? Number.MAX_SAFE_INTEGER : r.avgGapDays),
        className: 'do-htable__date',
        cell: r => (r.avgGapDays == null ? '—' : t('audit.everyDays', { n: r.avgGapDays })),
      },
      {
        key: 'lastDate',
        label: t('audit.colLast'),
        sortVal: r => r.lastDate,
        className: 'do-htable__date',
        cell: r => (r.lastDate
          ? <span>{fmtDateShort(r.lastDate)} <span className="do-audit__ago">{r.daysSince === 0 ? t('audit.today') : t('audit.daysAgo', { n: r.daysSince })}</span></span>
          : '—'),
      },
      {
        key: 'lastQty',
        label: t('audit.colQty'),
        sortVal: r => r.lastQty,
        className: 'do-htable__date',
        cell: r => (r.lastQty
          ? <span className="do-audit__qty" title={r.qtys.map(x => `${fmtDateShort(x.date)}: ${x.qty}`).join('\n')}>{r.lastQty}</span>
          : '—'),
      },
    ];
    if (showStore) {
      base.push({
        key: 'storeLabel',
        label: t('col.store'),
        className: 'do-htable__store',
        cell: r => (r.stores.length ? r.stores.map(s => s.value).join(', ') : '—'),
      });
    }
    return base;
  }, [t, ln, showStore, win, dueKeys]);

  const exportCsv = () => {
    if (rows.length) downloadCSV(AUDIT_CSV_HEADERS, auditToCsvRows(rows), `${config.id}-summary-${today}`);
  };

  const rangeLabel = custom
    ? `${from ? fmtDateShort(from) : t('audit.anyStart')} → ${to ? fmtDateShort(to) : fmtDateShort(today)}`
    : (days ? t('audit.lastDays', { n: days }) : t('audit.allTime'));

  return (
    <div className="do-audit">
      <div className="do-audit__controls">
        <DateRange presets={AUDIT_RANGE_OPTIONS} presetLabel={d => (d ? t('history.days', { n: d }) : t('audit.allTime'))}
          days={days} onPreset={pickPreset} from={from} to={to} onFrom={setFrom} onTo={setTo}
          onClear={() => { setFrom(''); setTo(''); }} max={today}
          fromAria={t('audit.fromAria')} toAria={t('audit.toAria')} clearLabel={t('audit.clearDates')} />
        <input className="do-history__search" type="search" placeholder={t('history.searchItems')} onFocus={keepInView}
          value={q} onChange={e => setQ(e.target.value)} aria-label={t('history.searchAria')} />
        <button className="do-history__export" onClick={exportCsv} disabled={!rows.length}
          title={t('audit.csvTitle')}>{t('common.csv')}</button>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={q || custom ? '🔍' : '📊'}
          title={q || custom ? t('history.noMatching') : t('audit.noneYet')}
          sub={q || custom ? t('audit.tryWider') : t('audit.noneHint', { verb: verb.toLowerCase() })} />
      ) : (
        <>
          {/* The point of the whole view: what the learned rhythm says is due now. */}
          {due.length > 0 && (
            <div className="do-due" role="region" aria-label={t('audit.dueTitle')}>
              <div className="do-due__head">
                <span className="do-due__title">🔁 {t('audit.dueTitle')}</span>
                <span className="do-due__sub">{t('audit.dueSub')}</span>
              </div>
              <div className="do-due__items">
                {due.slice(0, REORDER_SUGGEST_COUNT).map(r => (
                  <button key={r.key} className="do-due__item" onClick={() => onReorder(r)}
                    title={t('audit.dueAdd', { name: ln(r.name) })}>
                    <span className="do-due__name">＋ {ln(r.name)}</span>
                    <span className="do-due__meta">
                      {t('audit.dueMeta', { every: r.avgGapDays, since: r.daysSince })}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="do-kpis">
            <div className="do-kpi"><span className="do-kpi__val">{totals.orders}</span><span className="do-kpi__lbl">{t('audit.kpiOrders')}</span></div>
            <div className="do-kpi"><span className="do-kpi__val">{totals.items}</span><span className="do-kpi__lbl">{t('audit.kpiItems')}</span></div>
            <div className="do-kpi"><span className="do-kpi__val">{totals.days}</span><span className="do-kpi__lbl">{t('audit.kpiDays')}</span></div>
            <div className="do-kpi"><span className="do-kpi__val">{due.length}</span><span className="do-kpi__lbl">{t('audit.kpiDue')}</span></div>
          </div>

          <div className="do-audit__range-note">{t('audit.showing', { range: rangeLabel })}</div>

          {showStore && totals.stores.length > 0 && (
            <div className="do-trends__block do-audit__stores">
              <span className="do-trends__lbl">{t('audit.byStore')}</span>
              {totals.stores.slice(0, AUDIT_TOP_STORES).map(s => (
                <div key={s.value} className="do-trend-row">
                  <span className="do-trend-row__name">🏪 {s.value}</span>
                  <span className="do-trend-row__bar">
                    <span className="do-trend-row__fill" style={{ width: `${Math.round((s.count / totals.stores[0].count) * 100)}%` }} />
                  </span>
                  <span className="do-trend-row__val">{s.count}</span>
                </div>
              ))}
              <a className="do-trends__link" href={EXPENSE_ROUTE}>{t('history.trackSpend')}</a>
            </div>
          )}

          <AuditTable columns={cols} rows={rows} defaultSort={{ key: 'orders', dir: 'desc' }} rowKey={r => r.key}
            rowClass={r => (dueKeys.has(r.key) ? 'do-htable__row--due' : undefined)}
            renderDetail={r => <OrderDetail row={r} showStore={showStore} />} />
        </>
      )}
    </div>
  );
}
